import React, { useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    FormLabel,
    Flex,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form, FormikProps, FormikErrors } from 'formik';
import * as Yup from 'yup';

import CustomValidationControl from '../components/CustomValidationControl';

import TimeframeDetails from '../forms/LeaderboardConfigTimeframe';

import { JSONPreview } from '../components/JSONPreview';
import {
    LeaderboardDetail,
    TimeframeDetail,
    TimeframeEnum,
    TimeframeManualDetail,
} from '../interfaces/ILeaderboardConfig';
import * as LBCONFIG from '../consts/LeaderboardConfig';

import { Checklist } from '../components/Checklist';
import { getUnixDate } from '../util/datetime';

const timeframeDefault = {
    retention: 0,
    archive: false,
    rotation_trigger: getUnixDate(new Date()),
};

const timeframeManualDefault = {
    retention: 0,
    archive: false,
    start_dates: [getUnixDate(new Date())],
};

enum ScoreConfiType {
    insertonly = 'Insert Only',
    increment = 'Increment',
    highscore = 'High Score',
    lowscore = 'Low Score',
}

enum OrderOptions {
    ascending = 'Ascending',
    descending = 'Descending',
}

const Timeframes = [
    {
        label: 'Alltime',
        value: false,
        id: 'all',
        help: {
            title: 'All-time',
            content: 'The leaderboard will persist and never rotates',
        },
        children: undefined,
    },

    {
        label: 'Manual',
        value: false,
        id: 'manual',
        help: {
            title: 'Manual',
            content: 'Set your own schedule',
        },
        children: <></>,
    },

    {
        label: 'Yearly',
        value: false,
        id: 'yearly',
        children: <></>,
    },

    {
        label: 'Monthly',
        value: false,
        id: 'monthly',
        children: <></>,
    },

    {
        label: 'Weekly',
        value: false,
        id: 'weekly',
        children: <></>,
    },

    {
        label: 'Daily',
        value: false,
        id: 'daily',
        children: <></>,
    },
];

const scoreTypeOptions = Object.keys(ScoreConfiType).map((key) => {
    return {
        value: key,
        label: ScoreConfiType[key as keyof typeof ScoreConfiType].toString(),
    };
});

const orderOptions = Object.keys(OrderOptions).map((key) => {
    return {
        value: key,
        label: OrderOptions[key as keyof typeof OrderOptions].toString(),
    };
});

// function isTimeframeManualDetail(timeframe: any): timeframe is TimeframeManualDetail {
//     return typeof timeframe === 'object' && timeframe !== null && 'start_dates' in timeframe;
// }

// function isTimeframeDetail(timeframe: any): timeframe is TimeframeDetail {
//     return typeof timeframe === 'object' && timeframe !== null && 'rotation_trigger' in timeframe;
// }

const validationSchema = Yup.object({
    name: Yup.string()
        .required('Required')
        .matches(/[a-z0-9-_]+/, 'Invalid'),
    order: Yup.string().required('Required'),
    timeframes: Yup.object({
        daily: Yup.object({
            retention: Yup.number().required('Required').min(1, 'Retention must be greater than 1'),
        }).default(undefined),
        weekly: Yup.object({
            retention: Yup.number().required('Required').min(1, 'Retention must be greater than 1'),
        }).default(undefined),
        monthly: Yup.object({
            retention: Yup.number().required('Required').min(1, 'Retention must be greater than 1'),
        }).default(undefined),
        yearly: Yup.object({
            retention: Yup.number().required('Required').min(1, 'Retention must be greater than 1'),
        }).default(undefined),
        manual: Yup.object({
            retention: Yup.number().required('Required').min(1, 'Retention must be greater than 1'),
        }).default(undefined),
    }),
    score_config: Yup.object({
        type: Yup.string(),
        // TODO: commenting this out for now until the backend implements handling Capacity
        // capacity: Yup.number().when('type', {
        //     is: (val: string) => val === 'insertonly',
        //     then: (schema) =>
        //         schema
        //             .min(1, 'Retention must be greater than 1')
        //             .required('Retention must be greater than 1'),
        // }),
    }),
});

type LeaderboardConfigFormProps = {
    initialData: LeaderboardDetail;
    onSubmit: any;
    isSaved?: boolean;
};

const LeaderboardConfigForm: React.FC<LeaderboardConfigFormProps> = ({
    initialData,
    onSubmit,
    isSaved = false,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const helpRef = React.useRef(null);
    const [helperContent, setHelperContent] = useState('');
    const [helperTitle, setHelperTitle] = useState('');

    const openDrawer = (title: string, content: string) => {
        onOpen();

        setHelperContent(content);
        setHelperTitle(title);
    };

    let timeframeList = Timeframes;

    const [step, setStep] = useState(1);

    const handleNextStep = () => {
        setStep(step + 1);
    };

    const handlePrevStep = () => {
        setStep(step - 1);
    };

    const handleTabChange = (index: number) => {
        setStep(index + 1);
    };

    const displayScoreConfig = (formik: FormikProps<LeaderboardDetail>) => {
        return (
            <Box>
                <CustomValidationControl
                    type="select"
                    name="score_config.type"
                    value={formik.values.score_config.type}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    options={scoreTypeOptions}
                    label="Type"
                    data-group="score_config"
                    help={{
                        onClick: () =>
                            openDrawer('Score Config Type', LBCONFIG.HELP_SCORECONFIG_TYPE),
                    }}
                ></CustomValidationControl>
                {/* TODO: commenting this out for now until the backend implements handling Capacity
                 {formik.values.score_config.type === 'insertonly' && (
                    <CustomValidationControl
                        type="number"
                        name="score_config.capacity"
                        value={formik.values.score_config.capacity}
                        onChange={(num) => {
                            formik.setFieldValue('score_config.capacity', Number(num));
                        }}
                        isRequired={true}
                        label="Capacity"
                        data-group="score_config"
                        help={{
                            onClick: () =>
                                openDrawer('Capacity', LBCONFIG.HELP_SCORECONFIG_CAPACITY),
                        }}
                        validationMessage={
                            formik.touched.score_config?.capacity &&
                            formik.errors.score_config?.capacity
                                ? formik.errors.score_config?.capacity
                                : undefined
                        }
                    ></CustomValidationControl>
                )} */}
                <CustomValidationControl
                    type="checkbox"
                    name="score_config.has_data"
                    value={formik.values.score_config.has_data}
                    onChange={formik.handleChange}
                    label="Has Data"
                    data-group="has_data"
                    help={{
                        onClick: () => openDrawer('Has Data', LBCONFIG.HELP_SCORECONFIG_HAS_DATA),
                    }}
                ></CustomValidationControl>
                <CustomValidationControl
                    type="text"
                    name="data"
                    value={formik.values.data}
                    onChange={formik.handleChange}
                    label="Data"
                    isRequired={initialData?.score_config.has_data}
                    data-group="data"
                    help={{
                        onClick: () => openDrawer('Data', LBCONFIG.HELP_LEADERBOARD_DATA),
                    }}
                ></CustomValidationControl>
            </Box>
        );
    };

    const addChildren = (formik: FormikProps<LeaderboardDetail>) => {
        const yearlyDetail = formik.values.timeframes.yearly as TimeframeDetail;
        const monthlyDetil = formik.values.timeframes.monthly as TimeframeDetail;
        const weeklyDetail = formik.values.timeframes.weekly as TimeframeDetail;
        const dailyDetail = formik.values.timeframes.daily as TimeframeDetail;
        const manualDetail = formik.values.timeframes.manual as TimeframeManualDetail;
        timeframeList.map((st, idx) => {
            switch (st.id) {
                case 'manual':
                    return (st.children = (
                        <TimeframeDetails
                            info={manualDetail}
                            name="timeframes.manual"
                            timeframe={TimeframeEnum.MANUAL}
                            onChange={() => {}}
                            data-group="manual"
                            formik={formik}
                            onHelpClick={openDrawer}
                        ></TimeframeDetails>
                    ));
                case 'yearly':
                    return (st.children = (
                        <TimeframeDetails
                            name="timeframes.yearly"
                            info={yearlyDetail}
                            timeframe={TimeframeEnum.YEARLY}
                            onChange={() => {}}
                            data-group={'yearly'}
                            formik={formik}
                            onHelpClick={openDrawer}
                        ></TimeframeDetails>
                    ));
                case 'monthly':
                    return (st.children = (
                        <TimeframeDetails
                            name="timeframes.monthly"
                            info={monthlyDetil}
                            timeframe={TimeframeEnum.MONTHLY}
                            onChange={() => {}}
                            data-group={'monthly'}
                            formik={formik}
                            onHelpClick={openDrawer}
                        ></TimeframeDetails>
                    ));

                case 'weekly':
                    return (st.children = (
                        <TimeframeDetails
                            name="timeframes.weekly"
                            flex={1}
                            info={weeklyDetail}
                            timeframe={TimeframeEnum.WEEKLY}
                            onChange={() => {}}
                            data-group={'weekly'}
                            formik={formik}
                            onHelpClick={openDrawer}
                        ></TimeframeDetails>
                    ));

                case 'daily':
                    return (st.children = (
                        <TimeframeDetails
                            name="timeframes.daily"
                            flex={1}
                            info={dailyDetail}
                            timeframe={TimeframeEnum.DAILY}
                            onChange={() => {}}
                            data-group={'daily'}
                            formik={formik}
                            onHelpClick={openDrawer}
                        ></TimeframeDetails>
                    ));
            }
        });
    };

    const buildForm = () => {
        return (
            <>
                <Formik
                    onSubmit={(values) => onSubmit(values)}
                    enableReinitialize={true}
                    initialValues={initialData}
                    validationSchema={validationSchema}
                    initialErrors={{}}
                >
                    {(formik: FormikProps<LeaderboardDetail>) => {
                        addChildren(formik);
                        const dataToTimeframes = () => {
                            const newTimeframeList = [...timeframeList];
                            const newNew = newTimeframeList.map((tf, index) => {
                                if (tf.id === 'manual') {
                                    tf.value = !!formik.values.timeframes.manual;
                                }
                                if (tf.id === 'daily') {
                                    tf.value = !!formik.values.timeframes.daily;
                                }
                                if (tf.id === 'weekly') {
                                    tf.value = !!formik.values.timeframes.weekly;
                                }
                                if (tf.id === 'monthly') {
                                    tf.value = !!formik.values.timeframes.monthly;
                                }
                                if (tf.id === 'yearly') {
                                    tf.value = !!formik.values.timeframes.yearly;
                                }
                                if (tf.id === 'all') {
                                    tf.value = !!formik.values.timeframes.all;
                                }

                                return tf;
                            });
                            return newNew;
                        };

                        const handleCheckboxes = (
                            index: number,
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            const newSelectedTimeframes = [...timeframeList];
                            newSelectedTimeframes[index] = {
                                ...newSelectedTimeframes[index],
                                value: !newSelectedTimeframes[index].value,
                            };

                            const timeframe = newSelectedTimeframes[index].id;

                            const formikValue = (() => {
                                if (timeframe === 'all') return newSelectedTimeframes[index].value;
                                if (!newSelectedTimeframes[index].value) {
                                    return undefined;
                                }
                                switch (timeframe) {
                                    case 'all':
                                        return newSelectedTimeframes[index].value;
                                    case 'manual':
                                        return timeframeManualDefault;
                                    default:
                                        return timeframeDefault;
                                }
                            })();
                            formik.setValues(
                                {
                                    ...formik.values,
                                    timeframes: {
                                        ...formik.values.timeframes,
                                        [timeframe]: formikValue,
                                    },
                                },
                                false,
                            );
                        };
                        return (
                            <>
                                <Box flex={1} border={'1px solid grey'}>
                                    <Box padding={5}>
                                        <Form>
                                            <Tabs index={step - 1} onChange={handleTabChange}>
                                                <TabList>
                                                    <Tab
                                                        backgroundColor={
                                                            formik.errors.name
                                                                ? 'red.100'
                                                                : 'transparent'
                                                        }
                                                    >
                                                        Info
                                                    </Tab>
                                                    <Tab
                                                        backgroundColor={
                                                            formik.errors.timeframes
                                                                ? 'red.100'
                                                                : 'transparent'
                                                        }
                                                    >
                                                        Schedule
                                                    </Tab>
                                                    <Tab
                                                        backgroundColor={
                                                            formik.errors.score_config
                                                                ? 'red.100'
                                                                : 'transparent'
                                                        }
                                                    >
                                                        Scoring
                                                    </Tab>
                                                </TabList>
                                                <TabPanels>
                                                    <TabPanel>
                                                        <FormLabel>
                                                            Basic information for the Leaderboard
                                                            config.
                                                        </FormLabel>
                                                        <CustomValidationControl
                                                            name="name"
                                                            value={formik.values.name}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            label="Name"
                                                            isRequired={true}
                                                            validationMessage={
                                                                formik.touched.name &&
                                                                formik.errors.name
                                                                    ? formik.errors.name
                                                                    : undefined
                                                            }
                                                            help={{
                                                                onClick: () =>
                                                                    openDrawer(
                                                                        'Name',
                                                                        LBCONFIG.HELP_LEADERBOARD_NAME,
                                                                    ),
                                                            }}
                                                            readOnly={isSaved}
                                                        ></CustomValidationControl>
                                                        <CustomValidationControl
                                                            type="select"
                                                            name="order"
                                                            value={formik.values.order}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            options={orderOptions}
                                                            label="Order"
                                                            help={{
                                                                onClick: () =>
                                                                    openDrawer(
                                                                        'Order',
                                                                        LBCONFIG.HELP_LEADERBOARD_ORDER,
                                                                    ),
                                                            }}
                                                        ></CustomValidationControl>
                                                    </TabPanel>

                                                    <TabPanel>
                                                        <FormLabel>
                                                            The different time periods a leaderboard
                                                            is active and tracks scores.
                                                        </FormLabel>
                                                        {timeframeList && (
                                                            <Checklist
                                                                name="checklist"
                                                                onChange={handleCheckboxes}
                                                                list={dataToTimeframes()}
                                                                title="Timeframes"
                                                                help={{
                                                                    onClick: () =>
                                                                        openDrawer(
                                                                            'Timeframes',
                                                                            LBCONFIG.HELP_LEADERBOARD_TIMEFRAMES,
                                                                        ),
                                                                }}
                                                            ></Checklist>
                                                        )}
                                                    </TabPanel>

                                                    <TabPanel>
                                                        <FormLabel>
                                                            How the leaderboard adds new scores
                                                        </FormLabel>
                                                        {initialData?.score_config &&
                                                            displayScoreConfig(formik)}
                                                    </TabPanel>
                                                </TabPanels>
                                            </Tabs>
                                            <Flex justifyContent={'space-between'} mt={5}>
                                                <ButtonGroup>
                                                    <Button
                                                        onClick={handlePrevStep}
                                                        disabled={step === 1}
                                                        data-cy="previous-step"
                                                    >
                                                        Previous
                                                    </Button>
                                                    <Button
                                                        onClick={handleNextStep}
                                                        disabled={step === 3}
                                                        data-cy="next-step"
                                                    >
                                                        Next
                                                    </Button>
                                                </ButtonGroup>
                                                <ButtonGroup>
                                                    <Button type="submit">Save</Button>
                                                </ButtonGroup>
                                            </Flex>
                                        </Form>
                                    </Box>
                                </Box>
                                <Box flex={1}>
                                    {
                                        <JSONPreview
                                            data={formik.values}
                                            caption={'Leaderboard Config JSON:'}
                                        />
                                    }
                                </Box>
                            </>
                        );
                    }}
                </Formik>
                <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={helpRef}>
                    <DrawerOverlay />
                    <DrawerContent>
                        <DrawerCloseButton />
                        <DrawerHeader>{helperTitle}</DrawerHeader>

                        <DrawerBody whiteSpace="pre-wrap">{helperContent}</DrawerBody>
                    </DrawerContent>
                </Drawer>
            </>
        );
    };

    return buildForm();
};

export default LeaderboardConfigForm;
